import { useState } from 'react';
import { Avatar, Box, Flex } from '@chakra-ui/react';
import {
  Input,
  Modal,
  SectionLabel,
  SpinnerLoader,
  Text,
} from '@kula-ai/components';
import { useShowToast } from '@/hooks';
import { MODAL_Z_INDEX } from '@/constants/zIndex';
import { AnonymizeRecordsList } from './AnonymizeRecordsList';
import { useAnonymizeCandidate, useContactRecords } from './api';
import { consentErrorMessage } from './consentStatus';

export interface AnonymizeCandidate {
  id: number;
  first_name?: string;
  last_name?: string;
  /** Slider / row payloads carry a single `name` instead of first/last. */
  name?: string;
  image_url?: string;
  projects?: unknown[];
  outreaches?: unknown[];
  ats_applications?: unknown[];
}

const displayName = (candidate: AnonymizeCandidate) =>
  candidate.name ||
  `${candidate.first_name ?? ''} ${candidate.last_name ?? ''}`.trim();

interface AnonymizeCandidateModalProps {
  candidate: AnonymizeCandidate;
  modalActions: { isOpen: boolean; onClose: () => void };
  onSuccess?: () => void;
  // Query keys to refetch on success, so the anonymized state renders in place
  // without a manual refresh (CORE-5301).
  invalidateKeys?: unknown[][];
}

const DESCRIPTION_LEAD =
  "This permanently removes the candidate's personally identifiable information. They stay in your analytics as an anonymized candidate, so your metrics stay intact.";
const DESCRIPTION_WARNING = 'This cannot be undone.';

// The recruiter types this exact word to arm the destructive confirm.
const CONFIRM_KEYWORD = 'ANONYMIZE';

// Records-table anonymize confirm (mirrors DeleteCandidate) — no reason field.
export const AnonymizeCandidateModal = ({
  candidate,
  modalActions,
  onSuccess,
  invalidateKeys,
}: AnonymizeCandidateModalProps) => {
  const toast = useShowToast();
  const anonymize = useAnonymizeCandidate({ invalidateKeys });
  const [confirmText, setConfirmText] = useState('');
  const canConfirm = confirmText.trim() === CONFIRM_KEYWORD;
  const { id, image_url } = candidate;
  // Always fetch the authoritative records — the drawer passes only the current
  // application, not the candidate's full job/project/flow set, so we can't trust
  // the inline records. Fall back to the passed candidate until the fetch lands.
  const { data: fetched, isFetching } = useContactRecords(
    id,
    modalActions.isOpen
  );
  const isLoadingRecords = isFetching && !fetched;
  const records = (fetched ?? candidate) as AnonymizeCandidate;
  const name = displayName(candidate) || displayName(records ?? {});

  // The records table already surfaces active jobs/projects/flows, so clicking
  // confirm here IS the active-applications acknowledgement — send confirm
  // upfront rather than round-tripping a 422 for a second click.
  const handleConfirm = () =>
    anonymize.mutate(
      { target: { contactId: id }, confirm: true },
      {
        onSuccess: () => {
          toast({
            title: 'Candidate anonymized successfully',
            status: 'success',
          });
          onSuccess?.();
          modalActions.onClose();
        },
        onError: (err) =>
          toast({ title: consentErrorMessage(err), status: 'error' }),
      }
    );

  // kula's Modal header doesn't constrain the title to the dialog width, so a long
  // name used to overflow past the close button. Cap the title to the modal width
  // (responsive) and drop the name onto its own line where it truncates with a
  // tooltip (CORE-5465). The header width cap belongs upstream in @kula-ai/components.
  const title = (
    <Flex
      direction="column"
      gap="2px"
      minW={0}
      maxW="min(456px, calc(100vw - 7rem))"
    >
      <Flex gap="6px" alignItems="center" minW={0}>
        <Text variant="h4" className="whitespace-nowrap">
          Are you sure you want to anonymize
        </Text>
        <Avatar
          size="xs"
          name={name}
          src={image_url}
          borderRadius="6px"
          w="20px"
          h="20px"
          flexShrink={0}
        />
      </Flex>
      <Text variant="h4" truncate tooltip className="capitalize">
        {`${name}?`}
      </Text>
    </Flex>
  );

  const description = (
    <Text variant="p" color="body">
      {DESCRIPTION_LEAD}{' '}
      {/* Inline (same line) but p typography + semibold — `as="span"` keeps it in
          the paragraph without nesting a <p> in a <p>. */}
      <Text variant="p" as="span" className="font-semibold">
        {DESCRIPTION_WARNING}
      </Text>
    </Text>
  );

  return (
    <Modal
      open={modalActions.isOpen}
      onOpenChange={(open) => {
        if (!open) modalActions.onClose();
      }}
      size="md"
      className="!max-w-[540px]"
      // The kula overlay defaults below the candidate slide-over (z 1360), so the
      // backdrop never dimmed and it read as a pop-over. Raise it above the surface
      // so it behaves like a regular centered modal (CORE-5298).
      overlayZIndex={MODAL_Z_INDEX}
      title={title}
      // While the records load, show a single loader for the whole modal rather
      // than the description over an empty body (CORE-5283).
      description={isLoadingRecords ? undefined : description}
      primaryButton={{
        label: 'Anonymize candidate',
        variant: 'danger',
        onClick: handleConfirm,
        disabled: !canConfirm,
        isLoading: anonymize.isPending,
        loadingText: 'Anonymizing',
      }}
      secondaryButton={{ label: 'Cancel', variant: 'secondary' }}
    >
      {/* Grow with the records up to a cap, then scroll — rather than pinning a
          fixed height that scrolls even for a short list (CORE-5306). A minH keeps
          the loader/records swap from jumping. */}
      <Flex
        flexDir="column"
        minH="200px"
        maxH="min(520px, calc(100svh - 14rem))"
      >
        {isLoadingRecords ? (
          <Flex flex="1" justify="center" align="center">
            <SpinnerLoader size={24} />
          </Flex>
        ) : (
          <Flex flexDir="column" gap="20px" flex="1" minH="0" overflow="hidden">
            <Flex flexDir="column" flex="1" minH="0">
              <SectionLabel className="mb-2.5">Currently part of</SectionLabel>
              <Box flex="1" minH="0" overflowY="auto">
                <AnonymizeRecordsList candidate={records ?? candidate} />
              </Box>
            </Flex>

            <Box>
              <SectionLabel className="mb-2.5">Security input</SectionLabel>
              <Input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder={`Type "${CONFIRM_KEYWORD}" to confirm`}
              />
            </Box>
          </Flex>
        )}
      </Flex>
    </Modal>
  );
};
